<template>
  <v-app-bar
    class="app-bar justify-center ssr-fix-header"
    :height="height"
    :class="isOnHomepage ? 'ui-kit-bg-gray-0' : 'border-bottom'"
    flat
  >
    <v-container class="d-flex align-center">
      <slot name="prepend-logo" />
      <NuxtLink
        aria-label="Перейти на главную"
        :to="withRouteParams({ name: 'country-index' })"
      >
        <v-img
          height="32"
          alt="МедТочка"
          :src="locale.logo"
          :width="locale.isCis ? 215 : 150"
          @click="handleLogoClick"
        />
      </NuxtLink>

      <v-spacer />

      <div
        v-if="isTargetGroupButtonsVisible"
        class="mr-8 gc-8 d-none d-md-flex"
      >
        <NuxtLink
          v-for="button in headerButtons"
          :key="button.text"
          class="app-bar__header-button cursor-pointer
          text-landing-body-2 font-weight-semi-bold text-decoration-none"
          :class="{ 'text-primary': $route.name === button.type }"
          :to="button.to"
          replace
        >
          {{ button.text }}
        </NuxtLink>
      </div>

      <div class="d-flex flex-column">
        <BaseMtButton
          variant="tonal"
          class="ml-auto d-none d-sm-block app-bar__login-button"
          @click="handleClickLogin"
        >
          Войти
        </BaseMtButton>
        <BaseMtButton
          variant="tonal"
          class="ml-auto d-sm-none app-bar__login-button"
          small
          @click="handleClickLogin"
        >
          Войти
        </BaseMtButton>

        <v-menu
          v-model="isMenuActive"
          location="bottom"
          :close-on-content-click="false"
        >
          <v-card
            width="273"
            class="pa-4"
          >
            <div>
              <p class="text-subtitle-1 mb-2">
                МедТочка для пациента
              </p>
              <p class="mb-4 text-caption text-ui-kit-text-secondary">
                Управляйте записями и ведите медкарту
              </p>
              <BaseMtButton
                target="_blank"
                :href="authorizationUrl"
                small
                @click="isMenuActive = false"
              >
                Войти
              </BaseMtButton>
            </div>

            <v-divider class="mt-4" />

            <div class="mt-4">
              <p class="text-subtitle-1">
                МедТочка для врача
              </p>
              <p class="mt-2 mb-0 text-caption text-ui-kit-text-secondary">
                Просматривайте медкарты пациентов и отправляйте документы
              </p>
              <BaseMtButton
                class="mt-4"
                target="_blank"
                :href="locale.mtVrachUrl"
                small
                @click="isMenuActive = false"
              >
                Войти
              </BaseMtButton>
            </div>
          </v-card>
          <template #activator="{ props }">
            <span v-bind="props" />
          </template>
        </v-menu>
      </div>
    </v-container>
  </v-app-bar>
</template>

<script setup lang="ts">
import { useGoTo } from 'vuetify'
import { FEATURE_ROUTE_NAME } from '~/enums/features'

const { $featureFlag } = useNuxtApp()
const locale = useAppLocale()
const { height } = useHeaderHeight()
const goTo = useGoTo()

const headerButtons = [
  {
    to: withRouteParams({
      name: FEATURE_ROUTE_NAME.PATIENT,
      hash: '#feature-section',
    }),
    text: 'Пациентам',
    type: FEATURE_ROUTE_NAME.PATIENT,
  },
  ...(locale.isRu || $featureFlag('sff_vrach_cis')
    ? [{
      to: withRouteParams({
        name: FEATURE_ROUTE_NAME.DOCTOR,
        hash: '#feature-section',
      }),
      text: 'Врачам',
      type: FEATURE_ROUTE_NAME.DOCTOR,
    }]
    : []
  ),
  ...(locale.isRu
    ? [{
      to: withRouteParams({
        name: FEATURE_ROUTE_NAME.CLINIC,
        hash: '#feature-section',
      }),
      text: 'Клиникам',
      type: FEATURE_ROUTE_NAME.CLINIC,
    }]
    : []
  ),
]

const isMenuActive = ref(false)

const $route = useRoute()

const isOnHomepage = computed(() => $route.name?.toString().startsWith('country-index') ?? false)
const isTargetGroupButtonsVisible = computed(() => Object.values(FEATURE_ROUTE_NAME).includes($route.name as string))

const authorizationUrl = computed(
  () => `${useRuntimeConfig().public.mtUrl}/authorization/?preselected_country=${locale.currentCountry}`,
)

function handleLogoClick() {
  if ($route.name === 'country-index') {
    goTo(0)
  }
}

function handleClickLogin() {
  if (locale.isRu || $featureFlag('sff_vrach_cis')) {
    isMenuActive.value = true
  } else {
    window.location.assign(authorizationUrl.value)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/vuetify/settings';
@use 'sass:map';

.app-bar {
  &__header-button {
    color: $t-black;

    &:hover {
      color: $t-primary
    }
  }

  &__login-button {
    font-size: 14px;

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      font-size: 18px;
    }
  }
}

.border-bottom {
  border-bottom: 1px solid $t-ui-bg-gray-40;
}
</style>
